import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateFuelTypeCategoryRequest,
    FuelTypeCategoriesRequest,
    FuelTypeCategoriesResponse,
    FuelTypeCategory,
    UpdateFuelTypeCategoryRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type-category.api.ts';
import {
    createApiFuelTypeCategory,
    deleteApiFuelTypeCategory,
    getApiFuelTypeCategories,
    updateApiFuelTypeCategory,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type-category.api.ts';
import { extractError } from '@utils/slice.ts';

export interface FuelTypeCategoriesError {
    message?: string;
    [k: string]: any;
}

export interface FuelTypeCategoriesState {
    error: FuelTypeCategoriesError | null;

    commonFuelTypeCategories: FuelTypeCategoriesResponse | null;
    fuelTypeCategories: FuelTypeCategory[];
    pagination: CommonTablePaginatedResponse;

    updatedFuelTypeCategory: FuelTypeCategory | null;
    currentGetRequestId: string | null;

    isGetFuelTypeCategoriesPending: boolean;
    isCreateFuelTypeCategoryPending: boolean;
    isUpdateFuelTypeCategoryPending: boolean;
    isDeleteFuelTypeCategoryPending: boolean;
}

const initialState: FuelTypeCategoriesState = {
    error: null,

    commonFuelTypeCategories: null,
    fuelTypeCategories: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedFuelTypeCategory: null,
    currentGetRequestId: null,

    isGetFuelTypeCategoriesPending: false,
    isCreateFuelTypeCategoryPending: false,
    isUpdateFuelTypeCategoryPending: false,
    isDeleteFuelTypeCategoryPending: false,
};

export const getFuelTypeCategories = createAsyncThunk<
    FuelTypeCategoriesResponse,
    { clientId: number; tenantAlias: string; payload: FuelTypeCategoriesRequest },
    { rejectValue: FuelTypeCategoriesError }
>('adminCenterFuelTypeCategories/getFuelTypeCategories', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiFuelTypeCategories(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createFuelTypeCategory = createAsyncThunk<
    FuelTypeCategory,
    { clientId: number; tenantAlias: string; payload: CreateFuelTypeCategoryRequest },
    { rejectValue: FuelTypeCategoriesError }
>('adminCenterFuelTypeCategories/createFuelTypeCategory', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiFuelTypeCategory(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateFuelTypeCategory = createAsyncThunk<
    FuelTypeCategory,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateFuelTypeCategoryRequest },
    { rejectValue: FuelTypeCategoriesError }
>(
    'adminCenterFuelTypeCategories/updateFuelTypeCategory',
    ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
        updateApiFuelTypeCategory(clientId, tenantAlias, id, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const deleteFuelTypeCategory = createAsyncThunk<
    FuelTypeCategory,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: FuelTypeCategoriesError }
>('adminCenterFuelTypeCategories/deleteFuelTypeCategory', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiFuelTypeCategory(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const fuelTypeCategoriesSlice = createSlice({
    name: 'adminCenterFuelTypeCategories',
    initialState,
    reducers: {
        resetFuelTypeCategoriesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getFuelTypeCategories.pending, (state, action) => {
            state.isGetFuelTypeCategoriesPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getFuelTypeCategories.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFuelTypeCategoriesPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonFuelTypeCategories = action.payload;
            state.fuelTypeCategories = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getFuelTypeCategories.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFuelTypeCategoriesPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createFuelTypeCategory.pending, (state) => {
            state.isCreateFuelTypeCategoryPending = true;
            state.error = null;
        });
        builder.addCase(createFuelTypeCategory.fulfilled, (state, action) => {
            state.isCreateFuelTypeCategoryPending = false;
            state.updatedFuelTypeCategory = action.payload;
        });
        builder.addCase(createFuelTypeCategory.rejected, (state, action) => {
            state.isCreateFuelTypeCategoryPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateFuelTypeCategory.pending, (state) => {
            state.isUpdateFuelTypeCategoryPending = true;
            state.error = null;
        });
        builder.addCase(updateFuelTypeCategory.fulfilled, (state, action) => {
            state.isUpdateFuelTypeCategoryPending = false;
            state.updatedFuelTypeCategory = action.payload;
        });
        builder.addCase(updateFuelTypeCategory.rejected, (state, action) => {
            state.isUpdateFuelTypeCategoryPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteFuelTypeCategory.pending, (state) => {
            state.isDeleteFuelTypeCategoryPending = true;
            state.error = null;
        });
        builder.addCase(deleteFuelTypeCategory.fulfilled, (state, action) => {
            state.isDeleteFuelTypeCategoryPending = false;
            state.updatedFuelTypeCategory = action.payload;
            state.fuelTypeCategories = state.fuelTypeCategories.filter((c) => c.id !== action.payload.id);
        });
        builder.addCase(deleteFuelTypeCategory.rejected, (state, action) => {
            state.isDeleteFuelTypeCategoryPending = false;
            state.error = extractError(action);
        });
    },
});

export const { resetFuelTypeCategoriesState } = fuelTypeCategoriesSlice.actions;
export default fuelTypeCategoriesSlice.reducer;
